import React, { useState, useEffect, Dispatch, SetStateAction } from 'react';
import {
    CompetitionAdmin,
    CompetitionMatchManager,
} from '../../models/interfaces/Competitions/CompetitionAdmin';
import { Match, MatchStatus, MatchAdmin, ResultVerification } from '../../models/interfaces/Match';
import { CardColumns } from 'react-bootstrap';
import MatchStatsModal from './MatchStatsModal';
import ConfirmationModal from '../../components/containers/ConfirmationModal';
import { User } from '../../models/interfaces/User';
import { SportProvider } from '../../models/interfaces/SportProvider';
import { MatchSegmentStats } from '../../models/interfaces/Stat';
import MatchResultModal from './modules/MatchResultModal';
import UpdateMatchCrudModal from './UpdateMatchCrudModal';
import MatchOfficialVerificationModal from './modules/MatchOfficialVerificationModal';
import AdminCard from '../../components/containers/AdminCard';
import { AdminCardProps } from '../interfaces/AdminCardUI';

interface Props {
    competitionAdmin: CompetitionMatchManager;
    match: Match;
    onDelete: () => void;
    user: User;
    matchStatsList: MatchSegmentStats[] | undefined;
    sportProvider: SportProvider;
    triggerUpdatedMatchStats: () => void;
    finalScoreEntered: boolean;
}

export default function CompetitionMatchAdminView({
    competitionAdmin,
    match,
    onDelete,
    user,
    matchStatsList,
    sportProvider,
    triggerUpdatedMatchStats,
    finalScoreEntered,
}: Props) {
    const [showMatchStatsModal, setMatchStatsModal] = useState<boolean>(false);
    const [showMatchResultModal, setMatchResultModal] = useState<boolean>(false);
    const [showMatchCrudModal, setShowMatchCrudModal] = useState<boolean>(false);
    const [showConfirmDeleteMatchModal, setShowConfirmDeleteMatchModal] = useState<boolean>(false);
    const [showOfficialVerificationModal, setShowOfficialVerificationModal] =
        useState<boolean>(false);

    const [editMatchStats, setEditMatchStats] = useState<boolean>(
        match.status === MatchStatus.inProgress
    );

    const [hasLoadedMatchAdmin, setHasLoadedMatchAdmin] = useState<Boolean>(false);
    const [matchAdmin, setMatchAdmin] = useState<MatchAdmin | undefined>();
    const [deleteMatchError, setDeleteMatchError] = useState<string | undefined>(undefined);

    const [matchOfficialVerification, setMatchOfficialVerification] = useState<
        ResultVerification | undefined
    >(undefined);
    // need to manuel reload as object doesn't know if a new image has been provided
    const [reloadOfficialVerification, setReloadOfficialVerification] = useState<boolean>(false);

    useEffect(() => {
        if (match.status !== MatchStatus.inProgress) {
            setEditMatchStats(false);
        }
    }, [match.status]);

    const handleDelete = (setSubmitting: Dispatch<SetStateAction<boolean>>) => {
        setSubmitting(true);
        competitionAdmin
            .removeMatch(match)
            .then(() => {
                if (onDelete) {
                    onDelete();
                }
                setSubmitting(false);
            })
            .catch((error) => {
                console.error('remove match error', error);
                setDeleteMatchError('An error occurred please try again');
                setSubmitting(false);
            });
    };

    useEffect(() => {
        if (!hasLoadedMatchAdmin) {
            match
                .adminForUser(user)
                .then((matchAdmin) => {
                    setMatchAdmin(matchAdmin);
                })
                .catch((error) => {
                    console.log('adminError: ', error);
                    setMatchAdmin(undefined);
                })
                .finally(() => {
                    setHasLoadedMatchAdmin(true);
                });
        }
    }, [match, user, hasLoadedMatchAdmin]);

    const updateMatchAttributes = (
        matchDate: Date,
        onSuccess: () => void,
        onFailure: (string) => void
    ) => {
        matchAdmin
            ?.updateDate(matchDate)
            .then(() => onSuccess())
            .catch((error) => onFailure(error));
    };

    useEffect(() => {
        if (reloadOfficialVerification) {
            setReloadOfficialVerification(false);
        }
        if (match.status === MatchStatus.completed) {
            matchAdmin
                ?.getResultVerificationPromise()
                .then((resultVerificationObj) =>
                    setMatchOfficialVerification(resultVerificationObj)
                )
                .catch((error) => console.error(error));
        }
    }, [matchAdmin, reloadOfficialVerification, match.status]);

    useEffect(() => {
        if (match.status === MatchStatus.completed && matchStatsList) {
        }
    }, [match.status, matchStatsList]);

    const handleReloadOfficialVerification = () => {
        setReloadOfficialVerification(true);
    };

    if (!matchAdmin) return <div>an error occurred loading match admin user rights</div>;
    if (!matchStatsList) return <div>an error occurred initalizing match stats</div>;

    const manageMatchProps: AdminCardProps = {
        title: 'Manage Match',
        buttons: [
            {
                btnText: 'Edit Match Date',
                onClick: () => {
                    setShowMatchCrudModal(true);
                },
            },
            {
                btnText: 'Delete Match',
                onClick: () => setShowConfirmDeleteMatchModal(true),
                variant: 'danger',
            },
        ],
    };

    if (match.status === MatchStatus.scheduled) {
        manageMatchProps.buttons.unshift({
            btnText: 'Start Match',
            onClick: () => {
                matchAdmin.start();
            },
        });
    }

    const inProgressProps: AdminCardProps = {
        title: 'In Progress',
        buttons: [
            {
                btnText: 'Edit Stats',
                onClick: () => {
                    setMatchStatsModal((st) => !st);
                },
            },
        ],
    };
    if (matchStatsList.length === 0) {
        inProgressProps.buttons.unshift({
            btnText: 'Enter Final Score',
            onClick: () => {
                setMatchResultModal((st) => !st);
            },
            variant: 'outline-primary',
        });
    }

    const oldVerificationString = matchOfficialVerification?.invalidatedDate
        ? `Match updated on ${matchOfficialVerification.invalidatedDate.toLocaleDateString()} after verification was provided`
        : '';

    const completedMatchProps: AdminCardProps = {
        title: 'Post Match',
        buttons: [
            {
                btnText: matchOfficialVerification
                    ? 'Review Official Verification'
                    : 'Add Official Verification',
                onClick: () => {
                    setShowOfficialVerificationModal(true);
                },
                variant: 'secondary',
                toolTipText: oldVerificationString ? oldVerificationString : undefined,
            },
            {
                btnText: 'Update Match Stats',
                onClick: () => {
                    setEditMatchStats(true);
                    setMatchStatsModal((st) => !st);
                },
                variant: 'primary',
            },
        ],
    };

    // only be able to show view if match stats were provided and not just final score
    if (matchStatsList.length !== 0) {
        completedMatchProps.buttons.unshift({
            btnText: 'View Match Report',
            onClick: () => {
                if (editMatchStats) setEditMatchStats(false);
                setMatchStatsModal((st) => !st);
            },
        });
    }

    return (
        <div className="competitionMatchAdmin">
            <CardColumns>
                {match.status === MatchStatus.inProgress && <AdminCard {...inProgressProps} />}
                {match.status === MatchStatus.completed &&
                    completedMatchProps.buttons.length > 0 && (
                        <AdminCard {...completedMatchProps} />
                    )}
                <AdminCard {...manageMatchProps} />
            </CardColumns>
            <ConfirmationModal
                shouldShow={showConfirmDeleteMatchModal}
                onHideModal={() => {
                    setShowConfirmDeleteMatchModal(false);
                }}
                handleSubmit={handleDelete}
                title="Are you sure you want to delete the match?"
                btnText="Delete"
                btnVariant="danger"
                error={deleteMatchError}
            />
            <MatchStatsModal
                shouldShow={showMatchStatsModal}
                onHideModal={() => {
                    setMatchStatsModal(false);
                }}
                matchAdmin={matchAdmin}
                matchStatsList={matchStatsList}
                sportProvider={sportProvider}
                triggerUpdatedMatchStats={triggerUpdatedMatchStats}
                editMatchStats={editMatchStats}
                finalScoreEntered={finalScoreEntered}
            />
            <MatchResultModal
                shouldShow={showMatchResultModal}
                onHideModal={() => {
                    setMatchResultModal(false);
                }}
                matchAdmin={matchAdmin}
            />
            <MatchOfficialVerificationModal
                shouldShow={showOfficialVerificationModal}
                onHideModal={() => {
                    setShowOfficialVerificationModal(false);
                }}
                matchAdmin={matchAdmin}
                matchOfficialVerification={matchOfficialVerification}
                handleReloadOfficialVerification={handleReloadOfficialVerification}
            />
            {matchAdmin.match.competition && (
                <UpdateMatchCrudModal
                    shouldShow={showMatchCrudModal}
                    title="Edit Match Date"
                    submitBtnText="Update Match Date"
                    submitFunction={updateMatchAttributes}
                    hideModal={() => {
                        setShowMatchCrudModal(false);
                    }}
                    match={matchAdmin.match}
                />
            )}
        </div>
    );
}
